import type {
  CatalogV2ChannelMarket,
  CatalogV2ListRow,
} from '@skupreme/api-contracts/core';
import { marketplaceCountry } from '@features/storage-providers/lib/provider-brand';
import type { StatusDotTone } from '../components/v2/StatusDot';
import type { MarketplaceChannel } from './v2';

/**
 * M2: the health of a marketplace logo on the V2 catalog list, from what each channel reported per marketplace
 * (catalog.listing_content: its status word verbatim, its price, whether it still reports the listing) and the item's
 * own availability and state. One definition for the dot and its tooltip.
 */

export type ChannelStatusKind =
  | 'active'
  | 'inactive'
  | 'issue'
  | 'error'
  | 'unreported'
  | 'unrecognized';

/* unreported: the channel never sent a status (Gandalf 36, 10-31): a hollow grey dot, not green and not a warning. */
export type ChannelTone =
  | 'active'
  | 'warning'
  | 'unreported'
  | 'inactive'
  | 'error';

/* The status words the connection adapters write (Amazon, Shopify, Walmart, Temu, NetSuite), lower case. */
const STATUS_KINDS: Record<string, ChannelStatusKind> = {
  active: 'active',
  published: 'active',
  inactive: 'inactive',
  draft: 'inactive',
  archived: 'inactive',
  unpublished: 'inactive',
  unlisted: 'inactive',
  deleted: 'inactive',
  retired: 'inactive',
  ended: 'inactive',
  incomplete: 'issue',
  suppressed: 'issue',
  search_suppressed: 'issue',
  stranded: 'issue',
  system_problem: 'error',
};

/** A channel's status word as one of a few kinds, case-insensitive; null (or blank) is unreported. */
export const normalizeChannelStatus = (
  raw: string | null | undefined,
): ChannelStatusKind => {
  const word = raw?.trim().toLowerCase();
  if (!word) return 'unreported';
  return STATUS_KINDS[word] ?? 'unrecognized';
};

/* Strongest first: a logo takes the tone of its most urgent market. */
const TONE_ORDER: ChannelTone[] = [
  'error',
  'warning',
  'active',
  'unreported',
  'inactive',
];

export const CHANNEL_TONE_LABELS: Record<ChannelTone, string> = {
  active: 'Active',
  warning: 'Active with warnings',
  unreported: 'Status not reported yet',
  inactive: 'Inactive',
  error: 'Error',
};

/** The design-system tone the dot is drawn in (the shared Badge's). */
export const CHANNEL_TONE_BADGES: Record<ChannelTone, StatusDotTone> = {
  active: 'success',
  warning: 'caution',
  unreported: 'unreported',
  inactive: 'neutral',
  error: 'danger',
};

export type ChannelHealthItem = Pick<CatalogV2ListRow, 'available' | 'state'>;

export type ChannelHealth = {
  tone: ChannelTone;
  label: string;
  /** One line each, prefixed by the market's label when the logo covers more than one market. */
  warnings: string[];
};

type MarketHealth = {
  tone: ChannelTone;
  /** The channel lists it as live, so the item's own warnings apply. */
  live: boolean;
  warnings: string[];
};

/** "search_suppressed" -> "search suppressed". */
const plainWords = (status: string) =>
  status.trim().toLowerCase().replace(/_/g, ' ');

const marketHealth = (
  market: CatalogV2ChannelMarket,
  channelName: string,
): MarketHealth => {
  if (market.removed) {
    return {
      tone: 'inactive',
      live: false,
      warnings: [`No longer reported by ${channelName}`],
    };
  }
  const raw = market.channel_status ?? '';
  switch (normalizeChannelStatus(market.channel_status)) {
    case 'inactive':
      return { tone: 'inactive', live: false, warnings: [] };
    case 'error':
      return {
        tone: 'error',
        live: false,
        warnings: [`${channelName} reports a system problem`],
      };
    case 'issue':
      return {
        tone: 'warning',
        live: false,
        warnings: [`${channelName} reports this listing as ${plainWords(raw)}`],
      };
    case 'unrecognized':
      return {
        tone: 'warning',
        live: false,
        warnings: [`${channelName} reports status ${raw}`],
      };
    case 'unreported':
      return { tone: 'unreported', live: false, warnings: [] };
    default: {
      const priced = market.price != null && market.price.amount != null;
      return {
        tone: 'active',
        live: true,
        warnings: priced ? [] : ['No price'],
      };
    }
  }
};

/** The item's own warnings, for a market the channel lists as live. */
export const itemWarnings = (item: ChannelHealthItem): string[] => {
  const warnings: string[] = [];
  if (item.available === 0) warnings.push('0 available units');
  if (item.available === null) warnings.push('Available units unknown');
  if (item.state === 'discontinued' || item.state === 'archived') {
    warnings.push(`Item is ${item.state} in the catalog but still listed here`);
  }
  return warnings;
};

/** A market's name in the tooltip: its country, else its price currency, else its id; none for ''. */
export const marketLabel = (market: CatalogV2ChannelMarket) =>
  market.marketplace_id
    ? (marketplaceCountry(market.marketplace_id) ??
      market.price?.currency ??
      market.marketplace_id)
    : null;

/* A listing with no market reported (none sent by an older API, or no content row yet) has no status to show. */
const UNREPORTED_MARKET: CatalogV2ChannelMarket = {
  marketplace_id: '',
  channel_status: null,
  price: null,
  removed: false,
};

const marketsOfListing = (
  markets: CatalogV2ChannelMarket[] | undefined,
): CatalogV2ChannelMarket[] =>
  markets?.length ? markets : [UNREPORTED_MARKET];

/** A marketplace logo's health: all its listings' markets, the most urgent tone, every warning once. */
export const channelHealth = (
  channel: Pick<MarketplaceChannel, 'label' | 'listingMarkets'>,
  item: ChannelHealthItem,
): ChannelHealth => {
  const markets = channel.listingMarkets.flatMap(marketsOfListing);
  const marketIds = new Set(markets.map(market => market.marketplace_id));
  const labelled = marketIds.size > 1;
  const ownWarnings = itemWarnings(item);
  const tones = new Set<ChannelTone>();
  const warnings = new Set<string>();
  let anyLive = false;
  for (const market of markets) {
    const health = marketHealth(market, channel.label);
    const prefix = labelled ? marketLabel(market) : null;
    for (const warning of health.warnings) {
      warnings.add(prefix ? `${prefix}: ${warning}` : warning);
    }
    anyLive ||= health.live;
    tones.add(
      health.live && (health.warnings.length || ownWarnings.length)
        ? 'warning'
        : health.tone,
    );
  }
  if (anyLive) for (const warning of ownWarnings) warnings.add(warning);
  const tone = TONE_ORDER.find(candidate => tones.has(candidate)) ?? 'active';
  return { tone, label: CHANNEL_TONE_LABELS[tone], warnings: [...warnings] };
};

/** The whole state in words, for the logo's aria-label: "Storefront · Seed. Active with warnings. No price". */
export const channelHealthText = (channelText: string, health: ChannelHealth) =>
  [channelText, health.label, ...health.warnings].join('. ');
